'use client';

import { QueriesBoundary } from '@shared/api-utils';
import { useSession } from '@shared/auth';
import { useDebug } from '@shared/debug';
import { useClientConfig } from '@shared/utils';
import { useTranslations } from 'next-intl';
import { useEffect, type ComponentProps } from 'react';
import { PromotionsContent, PromotionsContentSkeleton, PromotionsEmpty, PromotionsList, type ContentType } from '../components';
import { usePromotionsFilter } from '../providers';
import type { PromoType } from '../types/types';
import { mapOffer } from '../utils/mapOffer/mapOffer';
import { useOffersData } from './hooks';

type PromoContentOffersProps = Omit<ComponentProps<typeof PromotionsContent>, 'promoType' | 'results'> & {
  showFilters: boolean;
  filteredVertical?: string[];
};

const PROMOTION_TYPE = 'offers' satisfies PromoType;

function Content({ showFilters, availableOffers, previousOffers }: { showFilters: boolean; availableOffers: Partial<ContentType>[]; previousOffers: Partial<ContentType>[] }) {
  const { filteredOffers, setPromotions } = usePromotionsFilter();

  useEffect(() => {
    if (showFilters) {
      setPromotions(availableOffers);
    }
  }, [availableOffers, setPromotions, showFilters]);

  const offers = showFilters ? filteredOffers : availableOffers;

  return (
    <>
      {offers.length ? <PromotionsList promoType={PROMOTION_TYPE} data={offers} /> : <PromotionsEmpty promoType={PROMOTION_TYPE} />}
      {previousOffers.length ? <PromotionsList promoType={PROMOTION_TYPE} data={previousOffers} showTitle grayscale /> : null}
    </>
  );
}

/**
 * Renders the promotions content for type = "offers". It fetches the available offers from the API
 * and maps them to the ContentType interface.
 * - Available offers are fetched from the private API if the user is logged in, otherwise from the public API.
 * - Previous offers are only fetched when the user is logged in.
 *
 * @param {boolean} props.showFilters - Whether offers are fed into and read from the promotions filter context.
 * @param {string[]} props.filteredVertical - Optional vertical filter for offers.
 * @returns {JSX.Element} The rendered promotions content for offers.
 */
function PromoContentOffers({ showFilters, filteredVertical, ...props }: PromoContentOffersProps) {
  const { session } = useSession();
  const isLoggedIn = session.status === 'authenticated';
  const { availableQuery, previousQuery } = useOffersData({ isLoggedIn, filteredVertical });
  const debug = useDebug();
  const t = useTranslations('offers.page');
  const { baseImgUrl } = useClientConfig();

  return (
    <PromotionsContent promoType={PROMOTION_TYPE} {...props}>
      <QueriesBoundary
        queries={[availableQuery, previousQuery]}
        loadingFallback={<PromotionsContentSkeleton />}
        errorFallbackRender={() => {
          debug.error(`Fetching failed in PromoContentOffers.`, { availableError: availableQuery.error, previousError: previousQuery.error });
          // TODO: This should probably be a dedicated error state, but replicating the previous empty state behaviour
          return <PromotionsEmpty promoType={PROMOTION_TYPE} />;
        }}
      >
        {([{ data: availableOffers }, { data: previousOffers }]) => {
          const mappedAvailableOffers = availableOffers.map(item => mapOffer({ item, t, isPreviousOffer: false, baseImgUrl }));
          const mappedPreviousOffers = previousOffers.slice(0, 5).map(item => mapOffer({ item, t, isPreviousOffer: true, baseImgUrl }));
          return <Content showFilters={showFilters} availableOffers={mappedAvailableOffers} previousOffers={mappedPreviousOffers} />;
        }}
      </QueriesBoundary>
    </PromotionsContent>
  );
}

export { PromoContentOffers };
